import { pickSeasonCircuitCalendar } from '@multirx/shared/game/seasonCircuitCalendar.js';
import type { SeasonMeetingCount } from '@multirx/shared/types/championship.js';
import { createPillButton } from '../ui/pillButton.js';
import {
  canStartSeasonWithCalendar,
  circuitPickerLabel,
  moveCalendarCircuit,
  toggleCircuitInCalendar,
  type CircuitPickerOption,
} from './seasonCircuitPickerLogic.js';

export interface SeasonCircuitPickerModel {
  meetingCount: SeasonMeetingCount;
  circuits: CircuitPickerOption[];
  calendar: number[];
}

export function renderSeasonCircuitPicker(
  model: SeasonCircuitPickerModel,
  onChange: (calendar: number[]) => void
): HTMLElement {
  const wrap = document.createElement('fieldset');
  wrap.className = 'championship-circuit-picker';

  const legend = document.createElement('legend');
  legend.textContent = 'Calendrier des circuits';
  wrap.appendChild(legend);

  const hint = document.createElement('p');
  hint.className = 'championship-circuit-picker-hint';
  hint.textContent = `Choisissez ${model.meetingCount} circuits distincts et définissez leur ordre (meeting 1 → ${model.meetingCount}).`;
  wrap.appendChild(hint);

  if (model.circuits.length < model.meetingCount) {
    const warn = document.createElement('p');
    warn.className = 'championship-circuit-picker-warn';
    warn.textContent = `Il faut au moins ${model.meetingCount} circuits enregistrés (actuellement ${model.circuits.length}).`;
    wrap.appendChild(warn);
    return wrap;
  }

  const toolbar = document.createElement('div');
  toolbar.className = 'championship-circuit-picker-toolbar';
  const randomBtn = createPillButton('Proposer un tirage', { variant: 'soft', small: true });
  if (randomBtn instanceof HTMLButtonElement) {
    randomBtn.type = 'button';
    randomBtn.onclick = () => {
      onChange(
        pickSeasonCircuitCalendar(
          model.circuits.map((c) => c.id),
          model.meetingCount
        )
      );
    };
  }
  toolbar.appendChild(randomBtn);
  wrap.appendChild(toolbar);

  const circuitById = new Map(model.circuits.map((c) => [c.id, c]));

  const ordered = document.createElement('ol');
  ordered.className = 'championship-circuit-order-list';
  ordered.setAttribute('aria-label', 'Ordre des meetings');

  for (let i = 0; i < model.meetingCount; i++) {
    const li = document.createElement('li');
    li.className = 'championship-circuit-order-item';
    const badge = document.createElement('span');
    badge.className = 'championship-circuit-order-badge';
    badge.textContent = `M${i + 1}`;
    const circuitId = model.calendar[i];
    const label = document.createElement('span');
    label.className = 'championship-circuit-order-label';
    if (circuitId != null) {
      const option = circuitById.get(circuitId);
      label.textContent = option ? circuitPickerLabel(option) : `Circuit #${circuitId}`;
    } else {
      label.textContent = '— à choisir —';
      label.classList.add('is-empty');
    }
    const actions = document.createElement('span');
    actions.className = 'championship-circuit-order-actions';
    if (circuitId != null) {
      const upBtn = createPillButton('↑', { variant: 'soft', small: true });
      const downBtn = createPillButton('↓', { variant: 'soft', small: true });
      const removeBtn = createPillButton('Retirer', { variant: 'soft', small: true });
      if (upBtn instanceof HTMLButtonElement) {
        upBtn.type = 'button';
        upBtn.disabled = i === 0;
        upBtn.onclick = () => onChange(moveCalendarCircuit(model.calendar, i, -1));
      }
      if (downBtn instanceof HTMLButtonElement) {
        downBtn.type = 'button';
        downBtn.disabled = i === model.calendar.length - 1;
        downBtn.onclick = () => onChange(moveCalendarCircuit(model.calendar, i, 1));
      }
      if (removeBtn instanceof HTMLButtonElement) {
        removeBtn.type = 'button';
        removeBtn.onclick = () =>
          onChange(toggleCircuitInCalendar(model.calendar, circuitId, model.meetingCount));
      }
      actions.append(upBtn, downBtn, removeBtn);
    }
    li.append(badge, label, actions);
    ordered.appendChild(li);
  }
  wrap.appendChild(ordered);

  const poolLegend = document.createElement('p');
  poolLegend.className = 'championship-circuit-pool-label';
  poolLegend.textContent = 'Circuits disponibles';
  wrap.appendChild(poolLegend);

  const pool = document.createElement('ul');
  pool.className = 'championship-circuit-pool-list';
  for (const circuit of model.circuits) {
    const li = document.createElement('li');
    const label = document.createElement('label');
    label.className = 'championship-circuit-pool-option';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = model.calendar.includes(circuit.id);
    const atCapacity =
      model.calendar.length >= model.meetingCount && !model.calendar.includes(circuit.id);
    input.disabled = atCapacity;
    input.addEventListener('change', () => {
      onChange(toggleCircuitInCalendar(model.calendar, circuit.id, model.meetingCount));
    });
    const text = document.createElement('span');
    text.textContent = circuitPickerLabel(circuit);
    label.append(input, text);
    li.appendChild(label);
    pool.appendChild(li);
  }
  wrap.appendChild(pool);

  return wrap;
}

export function isCircuitPickerComplete(model: SeasonCircuitPickerModel): boolean {
  return canStartSeasonWithCalendar(model.calendar, model.meetingCount, model.circuits.length);
}
